import { type JSX } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { getUserSecrets, expireUserSecret, deleteAllUserData, logout, type UserSecret } from '../../api/auth';
import { Button } from '../../components/Button';
import { useAuth } from '../../contexts/AuthContext';

export function MySecretsPage(): JSX.Element {
  const { user, refreshUser } = useAuth();
  const [secrets, setSecrets] = useState<UserSecret[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expiringId, setExpiringId] = useState<string | null>(null);
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    loadSecrets();
  }, []);

  async function loadSecrets(): Promise<void> {
    try {
      setLoading(true);
      setError(null);
      const data = await getUserSecrets();
      setSecrets(data);
    } catch (err) {
      setError('Failed to load your secrets');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  function handleCopySecretId(secretId: string): void {
    navigator.clipboard.writeText(secretId);
    setCopiedId(secretId);
    setTimeout(() => setCopiedId(null), 2000);
  }

  async function handleExpireSecret(secretId: string): Promise<void> {
    if (!confirm('Are you sure you want to expire this secret? This will delete all encrypted data and cannot be undone.')) {
      return;
    }

    try {
      setExpiringId(secretId);
      await expireUserSecret(secretId);
      // Reload the secrets to show updated status
      await loadSecrets();
    } catch (err) {
      alert('Failed to expire secret. Please try again.');
      console.error(err);
    } finally {
      setExpiringId(null);
    }
  }

  async function handleDeleteAllData(): Promise<void> {
    try {
      setDeletingAll(true);
      const result = await deleteAllUserData();

      // Log out the user
      await logout();
      await refreshUser();

      // Show success message and redirect
      alert(`All your data has been deleted. ${result.secrets_deleted} secret(s) were removed.`);
      window.location.href = '/';
    } catch (err) {
      alert('Failed to delete all data. Please try again.');
      console.error(err);
      setDeletingAll(false);
      setShowDeleteAllConfirm(false);
    }
  }

  function downloadAsJSON(): void {
    const dataStr = JSON.stringify(secrets, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `my-secrets-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  function formatDate(dateString: string): { date: string; time: string } {
    const d = new Date(dateString);
    return {
      date: d.toLocaleDateString(),
      time: d.toLocaleTimeString(),
    };
  }

  function formatTTL(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const days = Math.floor(hours / 24);

    if (days > 0) {
      return `${days} day${days > 1 ? 's' : ''}`;
    }
    if (hours > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''}`;
    }
    return `${seconds} second${seconds > 1 ? 's' : ''}`;
  }

  function getStatusBadgeColor(status: string): string {
    switch (status) {
      case 'pending_retrieval':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'burned_no_email':
      case 'burned_pending_email':
      case 'burned_email_sent':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
      case 'expired_no_email':
      case 'expired_pending_email':
      case 'expired_email_sent':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    }
  }

  function getStatusLabel(status: string): string {
    switch (status) {
      case 'pending_retrieval':
        return 'Active';
      case 'burned_no_email':
      case 'burned_pending_email':
      case 'burned_email_sent':
        return 'Retrieved';
      case 'expired_no_email':
      case 'expired_pending_email':
      case 'expired_email_sent':
        return 'Expired';
      default:
        return status;
    }
  }

  if (!user || !user.authenticated) {
    return (
      <div className="max-w-4xl mx-auto mt-8 p-6 bg-white dark:bg-gray-800 rounded-lg shadow-md [&_*]:transition-colors transition-colors">
        <h1 className="text-2xl font-bold mb-4 dark:text-white">My Secrets</h1>
        <p className="text-gray-600 dark:text-gray-300">Please log in to view your secrets.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto mt-8 p-6 bg-white dark:bg-gray-800 rounded-lg shadow-md [&_*]:transition-colors transition-colors">
        <h1 className="text-2xl font-bold mb-4 dark:text-white">My Secrets</h1>
        <p className="text-gray-600 dark:text-gray-300">Loading your secrets...</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto mt-8 p-6 bg-white dark:bg-gray-800 rounded-lg shadow-md [&_*]:transition-colors transition-colors">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold dark:text-white">My Secrets</h1>
        <div className="flex gap-2">
          {secrets.length > 0 && (
            <Button onClick={downloadAsJSON} variant="secondary">
              Download as JSON
            </Button>
          )}
          <Button onClick={() => setShowDeleteAllConfirm(true)} variant="danger">
            Delete All My Data
          </Button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded">
          {error}
        </div>
      )}

      {secrets.length === 0 && !error && (
        <p className="text-gray-600 dark:text-gray-300">You haven't created any secrets yet.</p>
      )}

      {secrets.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Secret ID
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <span title="Time to live">TTL</span>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Expires
                </th>
                <th className="px-1 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {secrets.map((secret) => {
                const createdDate = formatDate(secret.created_at);
                const expiresDate = formatDate(secret.expires_at);

                return (
                  <tr key={secret.secret_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 py-3 text-sm font-mono text-gray-900 dark:text-gray-100">
                      <div className="flex items-center gap-2">
                        <span title={secret.secret_id}>{secret.secret_id.substring(0, 8)}</span>
                        <button
                          onClick={() => handleCopySecretId(secret.secret_id)}
                          className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                          title="Copy full secret ID"
                        >
                          {copiedId === secret.secret_id ? (
                            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(secret.status)}`}>
                        {getStatusLabel(secret.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                      {formatTTL(secret.ttl)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                      <div className="flex flex-col">
                        <span>{createdDate.date}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">{createdDate.time}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                      <div className="flex flex-col">
                        <span>{expiresDate.date}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">{expiresDate.time}</span>
                      </div>
                    </td>
                    <td className="px-1 py-3 text-sm text-center">
                      {secret.burn_notification_enabled && (
                        <span className="text-orange-600 text-lg" title="Burn notification enabled">🔥</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {secret.status === 'pending_retrieval' && (
                        <Button
                          onClick={() => handleExpireSecret(secret.secret_id)}
                          variant="danger"
                          disabled={expiringId === secret.secret_id}
                        >
                          {expiringId === secret.secret_id ? 'Expiring...' : 'Expire'}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete All Confirmation Modal */}
      {showDeleteAllConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md mx-4 [&_*]:transition-colors transition-colors">
            <h2 className="text-xl font-bold mb-4 text-red-600 dark:text-red-400">Delete All My Data</h2>
            <p className="mb-4 text-gray-700 dark:text-gray-300">
              Are you absolutely sure you want to delete all your data? This will:
            </p>
            <ul className="list-disc list-inside mb-4 text-gray-700 dark:text-gray-300">
              <li>Delete all {secrets.length} of your secret{secrets.length !== 1 ? 's' : ''}</li>
              <li>Log you out of your account</li>
              <li>This action cannot be undone</li>
            </ul>
            <div className="flex gap-2 justify-end">
              <Button
                onClick={() => setShowDeleteAllConfirm(false)}
                variant="secondary"
                disabled={deletingAll}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteAllData}
                variant="danger"
                disabled={deletingAll}
              >
                {deletingAll ? 'Deleting...' : 'Yes, Delete Everything'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
